import { useRef, useState, type DragEvent } from "react";
import {
  AlertCircle,
  ArrowRight,
  FileSpreadsheet,
  History,
  Loader2,
  UploadCloud,
  X,
} from "lucide-react";
import type { CfoFundingReceipt } from "@/api/types";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { RECEIPT_STATES, fileSize } from "./fundingLabels";

const MAX_BYTES = 5 * 1024 * 1024;

function problem(file: File): string | null {
  if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
    return "Choose a CSV file (Crunchbase → Funding Rounds → Export).";
  }
  if (file.size > MAX_BYTES) return "The file is larger than 5 MB.";
  if (file.size === 0) return "The file is empty.";
  return null;
}

/** Step 1: drop or pick the Crunchbase Funding Rounds export. */
export function FundingUploadStep({
  canImport,
  busy,
  error,
  history,
  onPreview,
  onOpen,
}: {
  canImport: boolean;
  busy: boolean;
  error: string | null;
  history: CfoFundingReceipt[];
  onPreview: (file: File) => void;
  onOpen: (receipt: CfoFundingReceipt) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  function choose(next: File | null | undefined) {
    if (!next) return;
    const why = problem(next);
    setInvalid(why);
    setFile(why ? null : next);
  }

  function drop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragging(false);
    if (canImport && !busy) choose(event.dataTransfer.files?.[0]);
  }

  const message = invalid ?? error;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto p-6 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              if (canImport) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={drop}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors",
              dragging
                ? "border-indigo-500 bg-indigo-50/60"
                : "border-slate-200 bg-slate-50/60 hover:border-indigo-300 hover:bg-indigo-50/30",
              (!canImport || busy) && "pointer-events-none opacity-60",
            )}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200">
              <UploadCloud className="h-6 w-6" />
            </span>
            <span className="mt-4 text-sm font-medium text-slate-900">
              Drop your Crunchbase export here
            </span>
            <span className="mt-1 text-xs text-slate-500">
              or <span className="font-medium text-indigo-600">browse your files</span> · CSV,
              up to 5 MB and 1,000 rows
            </span>
            <input
              ref={input}
              type="file"
              accept=".csv,text/csv"
              aria-label="Funding CSV file"
              className="sr-only"
              disabled={!canImport || busy}
              onChange={(e) => {
                choose(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>

          {file && (
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <FileSpreadsheet className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500">{fileSize(file.size)} · ready to preview</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-400"
                aria-label="Remove the file"
                disabled={busy}
                onClick={() => setFile(null)}
              >
                <X />
              </Button>
            </div>
          )}

          {message && (
            <div
              role="alert"
              className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{message}</span>
            </div>
          )}

          {!canImport && (
            <p className="text-sm text-slate-500">
              Importing needs the right to import companies. You can still open past imports.
            </p>
          )}

          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-700">Expected file</p>
            <p className="mt-1">
              In Crunchbase, open a Funding Rounds search and export it to CSV. Required columns:
              Organization Name, Organization Name URL, Transaction Name URL, Funding Type and
              Announced Date. Nothing is recorded before you confirm the import.
            </p>
          </div>
        </div>

        <aside className="space-y-2">
          <h3 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
            <History className="h-3.5 w-3.5" />
            Recent imports
          </h3>
          {history.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400">
              No import yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {history.slice(0, 8).map((receipt) => {
                const state = RECEIPT_STATES[receipt.state] ?? {
                  label: receipt.state,
                  className: "bg-slate-100 text-slate-600",
                };
                return (
                  <li key={receipt.id}>
                    <button
                      type="button"
                      onClick={() => onOpen(receipt)}
                      className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-left transition-colors hover:border-indigo-200 hover:bg-indigo-50/40"
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-slate-800">
                          {receipt.filename}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                            state.className,
                          )}
                        >
                          {state.label}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[11px] text-slate-500">
                        {formatDate(receipt.created_at)} · {receipt.counters.rows ?? 0} rows ·{" "}
                        {receipt.counters.companies ?? 0} companies
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
        <Button
          variant="primary"
          disabled={!file || busy || !canImport}
          onClick={() => file && onPreview(file)}
        >
          {busy ? <Loader2 className="animate-spin" /> : null}
          {busy ? "Reading the file…" : "Preview data"}
          {!busy && <ArrowRight />}
        </Button>
      </div>
    </div>
  );
}
